import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Loader2, UserMinus, UserPlus } from 'lucide-react';
import { apiDel, apiGet, apiPost } from '../../lib/api.js';
import { fmtPhone } from '../../lib/format.js';

const STATUS_STYLE = {
  calling: 'bg-yellow-500/15 text-yellow-300 animate-pulse',
  connected: 'bg-green-500/15 text-green-300',
  ended: 'bg-white/10 text-white/40',
};

/**
 * "Add to Call" drawer: dial a third party (typed or picked from teammates /
 * the office line), merge them into the live call, and drop them again.
 * @param {Object} props
 * @param {string|null} props.callId - Active PhoneCall id.
 * @param {'ringing'|'in-progress'|string} props.callState
 * @param {{leadName?: string}|null} props.activeLead
 * @returns {JSX.Element}
 */
export default function ConferencePanel({ callId, callState, activeLead }) {
  const qc = useQueryClient();
  const [number, setNumber] = useState('');
  const [label, setLabel] = useState('');
  const key = ['phone-conference', callId];
  const connected = callState === 'in-progress';

  const { data: contactsData } = useQuery({
    queryKey: ['phone-conference-contacts'],
    queryFn: () => apiGet('/phone/conference/contacts'),
    staleTime: 60_000,
  });
  const contacts = contactsData?.data?.contacts || [];

  const { data } = useQuery({
    queryKey: key,
    queryFn: () => apiGet(`/phone/calls/${callId}/conference`),
    enabled: !!callId,
    refetchInterval: 2000,
  });
  const participants = (data?.data?.participants || []).map((p) => (
    p.role === 'lead' ? { ...p, label: activeLead?.leadName || 'Lead' } : p
  ));

  const add = useMutation({
    mutationFn: () => apiPost(`/phone/calls/${callId}/conference`, { to: number, label }),
    onSuccess: () => {
      toast.success('Calling… they will be merged when they answer');
      setNumber(''); setLabel('');
      qc.invalidateQueries({ queryKey: key });
    },
    onError: (e) => toast.error(e?.response?.data?.error || 'Could not add to the call'),
  });

  const drop = useMutation({
    mutationFn: (sid) => apiDel(`/phone/calls/${callId}/conference/${sid}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: key }),
    onError: () => toast.error('Could not remove that person'),
  });

  return (
    <div className="rounded-xl border border-civix-border bg-civix-panelLight/60 p-3 space-y-3" data-testid="conference-panel">
      <div className="text-xs font-semibold text-white/80 flex items-center gap-1.5">
        <UserPlus className="w-4 h-4 text-civix-purpleSoft" /> Add to call
      </div>

      {contacts.length > 0 && (
        <select
          className="w-full bg-civix-panel border border-civix-border rounded-lg px-3 py-2 text-xs text-white focus:outline-hidden focus:border-civix-purple"
          value=""
          onChange={(e) => {
            const c = contacts.find((x) => x.number === e.target.value);
            if (c) { setNumber(c.number); setLabel(c.label); }
          }}
          aria-label="Quick select"
        >
          <option value="">Quick select: teammate or office line…</option>
          {contacts.map((c) => <option key={`${c.kind}-${c.number}`} value={c.number}>{c.label} · {fmtPhone(c.number)}</option>)}
        </select>
      )}

      <div className="flex gap-2">
        <input
          type="tel"
          value={number}
          onChange={(e) => { setNumber(e.target.value); setLabel(''); }}
          onKeyDown={(e) => e.key === 'Enter' && number.trim() && connected && add.mutate()}
          placeholder="Phone number…"
          className="flex-1 min-w-0 bg-civix-panel border border-civix-border rounded-lg px-3 py-2 text-sm font-mono text-white placeholder:text-white/30 focus:outline-hidden focus:border-civix-purple"
        />
        <button
          onClick={() => add.mutate()}
          disabled={!number.trim() || !connected || add.isPending}
          className="px-3 rounded-lg bg-civix-purple hover:bg-civix-purpleDeep disabled:opacity-50 disabled:cursor-not-allowed text-white text-xs font-bold flex items-center gap-1.5 transition"
        >
          {add.isPending && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
          Call &amp; Merge
        </button>
      </div>
      {!connected && <p className="text-[11px] text-amber-300/80">Available once the lead answers.</p>}

      <ul className="space-y-1.5">
        {participants.map((p) => (
          <li key={p.callSid || p.role} className="flex items-center justify-between gap-2 text-xs">
            <span className="min-w-0 text-white/80 truncate">
              {p.label}{p.number ? <span className="text-white/40 font-mono"> · {fmtPhone(p.number)}</span> : null}
            </span>
            <span className="flex items-center gap-2 shrink-0">
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${STATUS_STYLE[p.status] || STATUS_STYLE.calling}`}>{p.status}</span>
              {p.role === 'third' && p.status !== 'ended' && (
                <button onClick={() => drop.mutate(p.callSid)} className="text-red-300 hover:text-red-200" title="Remove from call" aria-label={`Remove ${p.label}`}>
                  <UserMinus className="w-3.5 h-3.5" />
                </button>
              )}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
